import { trigger, transition, style, animate } from '@angular/animations';
import { Component, Inject, inject, ViewChild } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SharedService } from '@shared/services/shared.service';
import { InHomeCareChangeService } from 'app/services/license/in-home-care/in-home-care-change.service';
import { MY_DATE_FORMATS, MY_DATE_ADAPTER } from '../../license-date-format';
import { EntityOwnerComponent } from 'app/components/reusable/entity-owner/entity-owner.component';
import { DocumentComponent } from 'app/components/reusable/document/document.component';
import { IInHomeCareChange } from 'app/models/licenses/in-home-care/in-home-care-change';
import { Validators, FormControl, FormGroup } from '@angular/forms';
import { IInHomeCareLicense } from 'app/models/licenses/in-home-care/in-home-care-license';

export interface IIHCOwnerChangeDialog {
  licenseType: string;
  licenseId: number;
  licenseGuid: string;
  isSubunit: boolean;
  isRenewal: boolean;
  entityId: number;
  entityType: string;
  originalValue: any;
  newValue: any;
  changeAction: string;
}

@Component({
  selector: 'app-in-home-care-owner-change',
  standalone: false,
  templateUrl: './in-home-care-owner-change.component.html',
  styleUrl: './in-home-care-owner-change.component.scss',
  animations: [
      trigger('slideLeft', [
        transition(':enter', [
          style({ transform: 'translateX(-100%)' }),
          animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
        ]),
        transition(':leave', [
          animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
        ])
      ]),
      trigger('slideRight', [
        transition(':enter', [
          style({ transform: 'translateX(100%)' }),
          animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
        ]),
        transition(':leave', [
          animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
        ])
      ])
    ],
    providers: [
      { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
      MY_DATE_FORMATS,
      MY_DATE_ADAPTER,
    ]
})
export class InHomeCareOwnerChangeDialogComponent {
  @ViewChild(DocumentComponent) documents!: DocumentComponent;
  @ViewChild(EntityOwnerComponent) owners!: EntityOwnerComponent;

  public newChangeAdded: boolean = false;
  public change: IInHomeCareChange = {
    id: 0,
    licenseId: 0,
    licenseGuid: '',
    entity: '',
    type: '',
    originalValue: '',
    newValue: '',
    newParent: '',
    subunits: [],
    dateRequested: '',
    requestedBy: '',
    effectiveDateOfChange: '',
    signature: '',
    title: '',
    dateSigned: '',
    requiresPayment: false,
    approved: false,
    denied: false,
    determinationBy: '',
    dateOfDetermination: ''
  }

  public changeForm = new FormGroup({
    effectiveDateOfChange: new FormControl('', Validators.required),
  });

  public newValue: string = '';
  public validationErrors: string[] = [];
  public subunits: IInHomeCareLicense[] = [];
  public parentGuid: string = '';
  public selectedSubunits: string[] = [];
  public transferSubunits: boolean | null = null;
  public transferAllSubunits: boolean = false;
  public ownerDocumentTypes: string[] = ['Owner Agreement'];
  public thirtyDays: Date = new Date(Date.now());

  public sharedService = inject(SharedService);
  public changeService = inject(InHomeCareChangeService);
  public dialog = inject(MatDialog);

  constructor(@Inject(MAT_DIALOG_DATA) public changeData: IIHCOwnerChangeDialog,
    private dialogRef: MatDialogRef<InHomeCareOwnerChangeDialogComponent>) {
      this.thirtyDays.setDate(this.thirtyDays.getDate() + 30);
    }

  ngOnInit(): void {
    this.createChange();
    if(!this.changeData.isSubunit){
      this.getSubunits();
    }
  }

  createChange(): void {
    this.change.licenseId = this.changeData.licenseId;
    this.change.licenseGuid = this.changeData.licenseGuid;
    this.change.entity = this.changeData.entityType;
    this.change.type = this.changeData.changeAction;

    this.changeService.createChange(this.change).subscribe({
      next: change => {
        this.change = change;
      },
      error: e => {
        this.sharedService.toastr.error('Could Not Create Change');
        console.log('error creating change', e);
      },
      complete: () => {
        this.newChangeAdded = true;
      }
    });
  }

  selectSubunit(subunit: IInHomeCareLicense): void {
    if(this.selectedSubunits.includes(subunit.licenseId ?? '')){
      this.selectedSubunits = this.selectedSubunits.filter(s => s !== subunit.licenseId);
    }
    else{
      this.selectedSubunits.push(subunit.licenseId);
    }
  }

  validateParent(subunitId: string): boolean {
    if(this.selectedSubunits.includes(subunitId)){
      this.sharedService.openNotice('You cannot select a subunit that is also being transferred.');
      setTimeout(() => {
        this.parentGuid = null;
      }, 200);
      return false;
    }
    return true;
  }

  subunitSelected(licenseId: string): boolean {
    return this.selectedSubunits.includes(licenseId);
  }

  getSubunits(): void {
    this.changeService.getSubunits(this.changeData.licenseGuid).subscribe({
      next: subunits => {
        this.subunits = subunits;
      },
      error: e => {
        this.sharedService.toastr.error('Could Not Get Subunits');
        console.log('error getting subunits', e);
      }
    });
  }

  getEmptyChange(): IInHomeCareChange {
    return {
      id: 0,
      licenseId: 0,
      licenseGuid: '',
      entity: '',
      type: '',
      originalValue: '',
      newValue: '',
      newParent: '',
      subunits: [],
      dateRequested: '',
      requestedBy: '',
      effectiveDateOfChange: '',
      signature: '',
      title: '',
      dateSigned: '',
      requiresPayment: false,
      approved: false,
      denied: false,
      determinationBy: '',
      dateOfDetermination: ''
    }
  }

  updateChange(): void {
    this.change.newValue = this.changeData.newValue;
    this.change.subunits = this.selectedSubunits;
    this.change.newParent = this.parentGuid;
    let form = this.changeForm.value;
    this.change.effectiveDateOfChange = form.effectiveDateOfChange;
  }

  updateChangeForm(change: IInHomeCareChange): void {
    this.changeForm.reset({
      effectiveDateOfChange: change.effectiveDateOfChange
    });
  }

  submitChange(): void {
    this.updateChange();
    if(this.validateChange()){
      this.change.requiresPayment = !this.changeData.isRenewal;
      this.change.subunits = this.selectedSubunits;
      if(!this.changeData.isSubunit){
        let remainingSubunits = this.subunits.length - this.selectedSubunits.length;
        let updatedParent: string = null;
        if(remainingSubunits === 1){
          let newParent = this.subunits.find(s => !this.selectedSubunits.includes(s.licenseId));
          updatedParent = newParent.licenseId;
        }
        else if(remainingSubunits > 1){
          updatedParent = this.parentGuid;
        }

        if(!this.validateParent(updatedParent)){
          this.validationErrors.push('You must select a new parent agency for the remaining subunits');
          return;
        }

        this.change.newParent = updatedParent;
        setTimeout(() => {
          this.parentGuid = updatedParent;
        }, 200);
      }
      this.submit();
    }
    else{
      this.sharedService.openNotice('Your change request is missing information. Please verify all information before submitting.');
    }
  }

  submit(): void {
    this.changeService.submitChange(this.change).subscribe({
      error: e => {
        this.sharedService.toastr.error("Could not update change");
        console.log('error updating change', e);
      },
      complete: () => {
        this.newChangeAdded = false;
        this.sharedService.toastr.success("Change Request Submitted");
        this.close();
      }
    });
  }

  validateChange(): boolean {
    this.validationErrors = [];
    this.validationErrors.push(...this.owners.validateOwner());
    if(!this.changeData.isSubunit){
      this.validateSubunits();
    }
    let ownerAgreementFound = false;
    for (const document of this.documents.documents) {
      for (const type of document.types) {
        if (type.type === 'Owner Agreement') {
          ownerAgreementFound = true;
          break;
        }
      }
      if (ownerAgreementFound) break;
    }

    if (!ownerAgreementFound) {
      this.validationErrors.push('Missing owner agreement');
    }

    return this.validationErrors.length === 0;
  }

  validateSubunits(): void {
    let subunitDelta = this.subunits.length - this.selectedSubunits.length;
    if(subunitDelta > 1 && !this.parentGuid){
      this.validationErrors.push('You must select a new parent agency for the remaining subunits');
    }
  }

  deleteChange(change: IInHomeCareChange): void {
    this.changeService.deleteChange(change.id).subscribe({
      complete: () => {
        this.newChangeAdded = false;
        this.change = this.getEmptyChange();
      },
      error: e => {
        this.sharedService.toastr.error("Error Deleting Change");
        console.log('error deleting change', e);
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  cancel(): void {
    if(this.newChangeAdded){
      this.changeData.originalValue = 'close';
      setTimeout(() => {
        this.deleteChange(this.change);
      }, 1000);
    }
    this.close();
  }

}
